import { useState, useEffect, useRef } from 'react';

/**
 * Derives countdown from a Firebase timestamp + duration.
 * No stale closures — reads startTime and duration from args on each tick.
 *
 * @param {number|null} startTime - Unix ms when timer started
 * @param {number} duration - Timer duration in seconds
 * @param {function} onExpire - Called once when timer hits 0
 * @returns {{ remaining: number, isExpired: boolean }}
 */
export function useTimer(startTime, duration, onExpire) {
  const [remaining, setRemaining] = useState(duration);
  const [isExpired, setIsExpired] = useState(false);
  const expiredCalledRef = useRef(false);
  const onExpireRef = useRef(onExpire);
  const mountTimeRef = useRef(Date.now());

  // Keep onExpire ref current
  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  // Reset expired state when timer restarts
  useEffect(() => {
    expiredCalledRef.current = false;
    setIsExpired(false);
    mountTimeRef.current = Date.now();
  }, [startTime, duration]);

  useEffect(() => {
    if (!startTime) {
      setRemaining(duration);
      return;
    }

    const tick = () => {
      const elapsed = (Date.now() - startTime) / 1000;
      const left = Math.max(0, duration - elapsed);
      setRemaining(Math.ceil(left));

      if (left <= 0 && !expiredCalledRef.current) {
        // Don't fire onExpire if the timer was already expired when we started watching
        // (prevents stale timers from previous rounds triggering actions)
        const timeSinceWatch = (Date.now() - mountTimeRef.current) / 1000;
        if (timeSinceWatch < 2) {
          // Timer was already at 0 when we started — this is a stale timer, ignore
          // Show full duration instead of 0 so the UI doesn't freeze at red 0
          expiredCalledRef.current = true;
          setIsExpired(false);
          setRemaining(duration);
          return;
        }
        expiredCalledRef.current = true;
        setIsExpired(true);
        onExpireRef.current?.();
      }
    };

    tick(); // Immediate first tick
    const interval = setInterval(tick, 1000);

    return () => clearInterval(interval);
  }, [startTime, duration]);

  return { remaining, isExpired };
}
